import React, { Fragment, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography, makeStyles } from '@rbx/ui';
import { stickyBreadcrumbScrollOffset } from '@modules/ui/shared/utils/styles';
import { ReferenceType, MemberType, APIReferenceMember } from '@modules/client';
import {
  labelTranslKeyByMemberType,
  urlHashByMemberTypeDetailSection,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import { orderedMemberTypes } from '@modules/apiReference/page/utils/apiReferenceUtils';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';
import { getUniqueMemberTitleHashId } from '../../utils/apiReferencePageNavUtils';
import {
  GlobalReferenceProperties,
  GlobalReferenceFunctions,
  LibraryReferenceProperties,
  LibraryReferenceFunctions,
} from '../../types';
import ReferenceDivider from '../../sharedUI/components/ReferenceDivider';
import ReferenceMemberDetailContent from '../../sharedUI/components/ReferenceMemberDetailContent';

const useGlobalOrLibraryMemberDetailsStyles = makeStyles()(() => ({
  sectionTitle: {
    marginBottom: '36px',
    scrollMarginTop: stickyBreadcrumbScrollOffset,
  },
  memberList: {
    marginBottom: '48px',
  },
}));

interface GlobalOrLibraryMemberDetailsProps {
  properties: (GlobalReferenceProperties | LibraryReferenceProperties)[];
  functions: (GlobalReferenceFunctions | LibraryReferenceFunctions)[];
  referenceType: ReferenceType;
}

type GlobalOrLibraryMemberType = MemberType.Property | MemberType.Function;

const memberTypeToFieldName: Record<
  GlobalOrLibraryMemberType,
  keyof Omit<GlobalOrLibraryMemberDetailsProps, 'referenceType'>
> = {
  [MemberType.Property]: 'properties',
  [MemberType.Function]: 'functions',
};

function GlobalOrLibraryMemberDetails({
  properties,
  functions,
  referenceType,
}: GlobalOrLibraryMemberDetailsProps) {
  const { translate } = useTranslation();
  const { classes } = useGlobalOrLibraryMemberDetailsStyles();

  const membersByType = useMemo(
    () => ({ properties, functions }) as const,
    [properties, functions],
  );

  const existingSectionsOrderedMemberTypes = useMemo(() => {
    // Only process if this is a Global or Library reference type
    if (referenceType !== ReferenceType.GLOBAL && referenceType !== ReferenceType.LIBRARY) {
      return [];
    }

    const globalOrLibraryMemberTypes = orderedMemberTypes(
      referenceType,
    ) as GlobalOrLibraryMemberType[];
    return globalOrLibraryMemberTypes.filter((memberType) => {
      const fieldName = memberTypeToFieldName[memberType];
      const membersList = membersByType[fieldName];
      return membersList.length > 0;
    });
  }, [referenceType, membersByType]);

  if (existingSectionsOrderedMemberTypes.length === 0) return null;

  return (
    <Fragment>
      <ReferenceDivider />
      <Typography variant='h2' component='div' style={{ marginBottom: '24px' }}>
        {translate('Label.APIReference') || 'API Reference'}
      </Typography>
      {existingSectionsOrderedMemberTypes.map((memberType) => {
        const headerTranslKey = labelTranslKeyByMemberType[memberType];
        const sectionId = urlHashByMemberTypeDetailSection[memberType].slice(1);
        const fieldName = memberTypeToFieldName[memberType];
        const members = membersByType[fieldName];

        return (
          <Fragment key={memberType}>
            <Typography
              id={sectionId}
              variant='largeLabel2'
              component='div'
              className={classes.sectionTitle}>
              {translate(headerTranslKey) || headerTranslKey}
            </Typography>
            <section className={classes.memberList}>
              {members.map((member, i) => {
                const prevMember = members[i - 1];
                const isSameNameAsPrevMember = !!prevMember && member.name === prevMember.name;

                const id = getUniqueMemberTitleHashId(
                  member as unknown as APIReferenceMember,
                  isSameNameAsPrevMember,
                );
                const allTags = getVisibleReferenceTags({ tags: member.tags });

                const isFunction = memberType === MemberType.Function;
                const params = isFunction
                  ? (member as GlobalReferenceFunctions | LibraryReferenceFunctions).parameters
                  : undefined;
                const fallbackKey =
                  Array.isArray(params) && params.length > 0
                    ? `${member.name}-${params.map((p) => p.name).join('-')}`
                    : `${member.name}-${memberType}`;
                const memberKey = id || fallbackKey;

                return (
                  <ReferenceMemberDetailContent
                    key={memberKey}
                    name={member.name}
                    summary={member.summary}
                    allTags={allTags}
                    description={member.description}
                    deprecationMessage={undefined}
                    member={member}
                    memberType={memberType}
                    codeSamples={
                      (member as GlobalReferenceFunctions | LibraryReferenceFunctions).codeSamples
                    }
                    id={id}
                  />
                );
              })}
            </section>
          </Fragment>
        );
      })}
    </Fragment>
  );
}

export default GlobalOrLibraryMemberDetails;
